import React from 'react';
import {
  FileText, Folder, Database, Presentation, Network, Globe, History,
  FileText as FileTextDefault,
} from 'lucide-react';

/**
 * DocumentsBrowser — list or grid view of a project's root documents.
 *
 * Single source of truth for the icon/color/label per document type. Mode is
 * driven from outside (ui_preferences.documents_view_mode) so the user's
 * preferred view sticks across devices.
 */

// labelKey is a translation key.
const TYPE_META = {
  document:    { icon: FileText,     color: 'text-blue-400',   labelKey: 'projects.doc_types.document' },
  folder:      { icon: Folder,       color: 'text-yellow-400', labelKey: 'projects.doc_types.folder' },
  database:    { icon: Database,     color: 'text-cyan-400',   labelKey: 'projects.doc_types.database' },
  whiteboard:  { icon: Presentation, color: 'text-green-400',  labelKey: 'projects.doc_types.whiteboard' },
  mindmap:     { icon: Network,      color: 'text-pink-400',   labelKey: 'projects.doc_types.mindmap' },
  embed:       { icon: Globe,        color: 'text-purple-400', labelKey: 'projects.doc_types.embed' },
  changelog:   { icon: History,      color: 'text-amber-700 dark:text-amber-400',  labelKey: 'projects.doc_types.changelog' },
};

const metaFor = (type) => TYPE_META[type] || { icon: FileTextDefault, color: 'text-blue-400', labelKey: 'projects.doc_types.document' };

const DocumentsBrowser = ({ docs, mode, onOpen, formatDate, t }) => {
  if (!docs || docs.length === 0) {
    return (
      <div className="bg-white dark:bg-white/5 rounded-lg border border-border dark:border-border px-4 py-8 text-center">
        <FileText className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
        <p className="text-sm text-muted-foreground">
          {t('projects.documents_empty', { defaultValue: 'No documents yet.' })}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">
          {t('projects.documents_empty_hint', { defaultValue: 'Use New to add a document, database, or another type.' })}
        </p>
      </div>
    );
  }

  if (mode === 'grid') {
    return <GridView docs={docs} onOpen={onOpen} formatDate={formatDate} t={t} />;
  }
  return <ListView docs={docs} onOpen={onOpen} formatDate={formatDate} t={t} />;
};

export default DocumentsBrowser;

// ─── List view (TasksTable-style — bg-white / divide-y / no per-cell borders) ─

const ListView = ({ docs, onOpen, formatDate, t }) => (
  <div className="bg-white dark:bg-white/5 rounded-lg border border-border dark:border-border overflow-hidden">
    {/* Header row */}
    <div className="hidden sm:grid gap-3 px-3 py-1.5 bg-muted dark:bg-white/[0.03] border-b border-border dark:border-border text-[11px] text-muted-foreground/70 uppercase tracking-wider"
      style={{ gridTemplateColumns: '1fr 120px 140px' }}
    >
      <div>{t('projects.documents_col_name', { defaultValue: 'Name' })}</div>
      <div>{t('projects.documents_col_type', { defaultValue: 'Type' })}</div>
      <div>{t('projects.documents_col_modified', { defaultValue: 'Last edited' })}</div>
    </div>
    <div className="divide-y divide-border dark:divide-border/50">
      {docs.map(doc => {
        const meta = metaFor(doc.type || 'document');
        const Icon = meta.icon;
        const updatedAt = doc.updated_at || doc.created_at;
        return (
          <button
            key={doc.id}
            onClick={() => onOpen(doc.id)}
            className="w-full grid gap-3 px-3 py-2 hover:bg-muted dark:hover:bg-white/[0.03] transition-colors text-left items-center"
            style={{ gridTemplateColumns: '1fr 120px 140px' }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Icon className={`w-4 h-4 ${meta.color} flex-shrink-0`} />
              <span className="text-sm text-foreground truncate">
                {doc.title || t('documents.untitled', { defaultValue: 'Untitled' })}
              </span>
            </div>
            <div className="text-xs text-muted-foreground capitalize truncate">
              {t(meta.labelKey)}
            </div>
            <div className="text-xs text-muted-foreground/70 truncate">
              {updatedAt ? formatDate(updatedAt) : ''}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// ─── Grid view (file-explorer tiles) ─────────────────────────────────────────

const GridView = ({ docs, onOpen, formatDate, t }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
    {docs.map(doc => {
      const meta = metaFor(doc.type || 'document');
      const Icon = meta.icon;
      const updatedAt = doc.updated_at || doc.created_at;
      return (
        <button
          key={doc.id}
          onClick={() => onOpen(doc.id)}
          className="bg-white dark:bg-white/5 rounded-lg border border-border dark:border-border hover:border-amber-500/50 hover:bg-muted dark:hover:bg-white/[0.04] transition-colors p-3 text-left flex flex-col gap-2 group min-w-0"
        >
          {/* Big icon centred on its own colour-tinted disk — matches the
              vibe of native file-explorer thumbnails without over-reaching. */}
          <div className="flex items-center justify-center h-16 rounded-md bg-muted dark:bg-white/[0.03] group-hover:bg-white dark:group-hover:bg-white/[0.06] transition-colors">
            <Icon className={`w-8 h-8 ${meta.color}`} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground truncate group-hover:text-amber-500 transition-colors">
              {doc.title || t('documents.untitled', { defaultValue: 'Untitled' })}
            </div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 truncate">
                {t(meta.labelKey)}
              </span>
              {updatedAt && (
                <span className="text-[10px] text-muted-foreground/50 truncate">
                  {formatDate(updatedAt)}
                </span>
              )}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);
